import { useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import {
    AutoAwesomeRounded as SparkleIcon,
    CheckCircleRounded as CheckIcon,
    HelpOutlineRounded as HelpIcon,
    HourglassTopRounded as PendingIcon,
    ImageOutlined as ImageIcon,
    LinkRounded as BioIcon,
    PostAddRounded as PostIcon,
    ReplayRounded as ReplayIcon,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { requestAutomationVerification } from '@/services/dmAutomation.service';
import { uploadImageToStorage } from '@/services/proxy.service';
import { queryKeys } from '@/hooks/queries/keys';
import type { AutomationVerification, AutomationVerifyMethod } from '@/types/dmAutomation';
import { ACCENT, ACCENT_HOVER, ACCENT_TINT, PageHeader } from './_shared';

interface Props {
    verification: AutomationVerification;
    onGoToProfileLink?: () => void;
    /** 인스타 미연동 시 기존 인스타 연동 화면으로 보내기 (연동 기능은 이미 구현돼 있어 재사용) */
    onGoToInstagram?: () => void;
}

const METHODS: { key: AutomationVerifyMethod; icon: React.ReactNode; title: string; desc: string }[] = [
    {
        key: 'POST',
        icon: <PostIcon />,
        title: '게시물로 인증',
        desc: '우리 프로필링크를 인스타그램에 게시물로 올렸어요',
    },
    {
        key: 'BIO',
        icon: <BioIcon />,
        title: '프로필 링크로 인증',
        desc: '인스타 프로필 링크를 우리 프로필링크로 바꿨어요',
    },
];

const fmtDate = (iso: string | null) => (iso ? iso.slice(0, 10) : null);

/** 혜택 안내 히어로 (모든 상태 공통) */
const BenefitHero = () => (
    <Box
        sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: 2.5,
            color: '#fff',
            background: 'linear-gradient(135deg, #241a17 0%, #4a2c22 52%, #b6472a 128%)',
            boxShadow: '0 16px 44px rgba(180,60,30,0.28)',
        }}
    >
        <Box
            sx={{
                position: 'absolute',
                top: -70,
                right: -50,
                width: 220,
                height: 220,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${ACCENT}66, transparent 70%)`,
                pointerEvents: 'none',
            }}
        />
        <Stack spacing={1.5} sx={{ position: 'relative' }}>
            <Chip
                icon={<SparkleIcon sx={{ fontSize: 16 }} />}
                label="인증 회원 전용 혜택"
                sx={{
                    alignSelf: 'flex-start',
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: '#fff',
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: '#fff' },
                }}
            />
            <Typography sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 900, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
                인증하면 오토DM
                <br />
                <Box component="span" sx={{ color: '#FFC9B4' }}>무료 500개 + 무한 리필</Box>
            </Typography>
            <Typography sx={{ fontSize: { xs: 14, md: 15 }, color: 'rgba(255,255,255,0.82)', lineHeight: 1.65, maxWidth: 520 }}>
                우리 프로필링크를 <b>인스타에 게시물로 올리거나</b>, <b>프로필 링크를 우리 링크로 바꾸면</b> 인증 대상이에요.
                둘 중 하나만 하면 됩니다.
            </Typography>
        </Stack>
    </Box>
);

const VerificationScreen = ({ verification, onGoToProfileLink, onGoToInstagram }: Props) => {
    const qc = useQueryClient();
    const [method, setMethod] = useState<AutomationVerifyMethod>(verification.method ?? 'POST');
    const [proof, setProof] = useState('');
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const status = verification.status;
    const showForm = status === 'NONE' || status === 'REJECTED' || status === 'REVOKED';

    const mutation = useMutation({
        mutationFn: () => requestAutomationVerification({ method, proof: proof || undefined }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.dmAutomation.verification() });
            qc.invalidateQueries({ queryKey: queryKeys.dmAutomation.limit() });
            toast.success('인증을 요청했어요. 관리자 확인 후 열려요.');
        },
        onError: (e: any) => toast.error(e?.data?.message || e?.message || '인증 요청에 실패했어요'),
    });

    const uploadProof = async (file?: File) => {
        if (!file) return;
        setUploading(true);
        try {
            const res = await uploadImageToStorage([file]);
            const url = res?.url?.length ? res.url[0] : undefined;
            if (!url) {
                toast.error('이미지 업로드에 실패했어요');
                return;
            }
            setProof(url);
        } catch {
            toast.error('이미지 업로드 중 오류가 발생했어요');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 760 }}>
            <PageHeader title="오토DM 인증" desc="인증하면 오토DM 무료 혜택이 열려요. 인증은 인스타 계정 기준이에요." />

            {/* 상태 배너 */}
            {status === 'PENDING' && (
                <Alert
                    icon={<PendingIcon />}
                    severity="info"
                    variant="outlined"
                    sx={{ borderRadius: 3, mb: 2.5, alignItems: 'center' }}
                >
                    <Typography sx={{ fontWeight: 700 }}>인증 심사 중이에요</Typography>
                    <Typography variant="body2" color="text.secondary">
                        관리자가 인스타 프로필을 확인한 뒤 승인하면 오토DM이 열려요. 보통 <b>영업일 기준 2~5일</b> 내에 확인돼요.
                        {fmtDate(verification.requestedAt) ? ` (요청: ${fmtDate(verification.requestedAt)})` : ''}
                    </Typography>
                </Alert>
            )}
            {status === 'REJECTED' && (
                <Alert severity="error" variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>인증이 반려됐어요</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {verification.rejectReason || '사유가 없습니다. 조건을 확인하고 다시 인증해 주세요.'}
                    </Typography>
                </Alert>
            )}
            {status === 'REVOKED' && (
                <Alert severity="warning" variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
                    <Typography sx={{ fontWeight: 700 }}>인증 자격이 해제됐어요</Typography>
                    <Typography variant="body2" color="text.secondary">
                        조건을 다시 갖춘 뒤 재인증하면 오토DM을 계속 쓸 수 있어요.
                    </Typography>
                </Alert>
            )}

            <BenefitHero />

            {showForm && (
                <Box
                    sx={{
                        bgcolor: '#fff',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 3,
                        p: { xs: 2.5, md: 3 },
                    }}
                >
                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.5 }}>
                        {status === 'NONE' ? '어떻게 인증하실 건가요?' : '다시 인증하기'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        하실 방법을 골라주세요. 관리자가 인스타 프로필을 직접 확인해 승인해요.
                    </Typography>

                    {/* 연동 인증 — 인증은 "우리에 연동된 인스타 계정" 기준으로만 진행된다(읽기전용 표기). */}
                    {verification.hasInstagram && verification.instagramUsername && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                                bgcolor: ACCENT_TINT,
                                border: '1px solid rgba(0,0,0,0.06)',
                                borderRadius: 2,
                                px: 1.75,
                                py: 1.25,
                                mb: 2,
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                                연동 인증
                            </Typography>
                            <Tooltip
                                arrow
                                enterTouchDelay={0}
                                leaveTouchDelay={4000}
                                title={
                                    <Box sx={{ p: 0.5, lineHeight: 1.7 }}>
                                        인증은 <b>소듀에 연동된 인스타 계정</b>으로만 진행돼요.
                                        <br />
                                        프로필링크는 누구나 열어볼 수 있다 보니, 다른 분의 링크로 인증되는 일을 막기 위해
                                        <b> 연동된 계정 = 인증 계정</b>이 같아야 해요.
                                        <br />
                                        다른 계정으로 인증하시려면 <b>인스타 연결</b>에서 계정을 바꿔주세요.
                                    </Box>
                                }
                            >
                                <HelpIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
                            </Tooltip>
                            <Chip
                                size="small"
                                label={`@${verification.instagramUsername}`}
                                sx={{ fontWeight: 800, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.1)' }}
                            />
                            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                                이 계정으로 확인해요
                            </Typography>
                        </Box>
                    )}

                    {!verification.hasInstagram && (
                        <Alert
                            severity="warning"
                            sx={{ borderRadius: 2, mb: 2 }}
                            action={
                                onGoToInstagram ? (
                                    <Button color="inherit" size="small" onClick={onGoToInstagram}>
                                        인스타 연동하기
                                    </Button>
                                ) : undefined
                            }
                        >
                            인스타그램 계정을 먼저 연동해야 인증을 요청할 수 있어요.
                        </Alert>
                    )}

                    {/* 프로필링크는 가입 시 자동 생성되어 대부분 보유(계약 §7) — 관문이 아니라 참고 안내만 */}
                    {!verification.hasProfileLink && (
                        <Alert
                            severity="info"
                            sx={{ borderRadius: 2, mb: 2 }}
                            action={
                                onGoToProfileLink ? (
                                    <Button color="inherit" size="small" onClick={onGoToProfileLink}>
                                        프로필링크 보기
                                    </Button>
                                ) : undefined
                            }
                        >
                            인스타에 올릴 내 프로필링크를 확인해보세요. 인증 요청은 지금도 가능해요.
                        </Alert>
                    )}

                    {/* 방법 선택 */}
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2.5 }}>
                        {METHODS.map((m) => {
                            const selected = method === m.key;
                            return (
                                <Box
                                    key={m.key}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setMethod(m.key)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') setMethod(m.key);
                                    }}
                                    sx={{
                                        flex: 1,
                                        position: 'relative',
                                        p: 2,
                                        borderRadius: 2.5,
                                        cursor: 'pointer',
                                        border: '1.5px solid',
                                        borderColor: selected ? ACCENT : 'divider',
                                        bgcolor: selected ? ACCENT_TINT : 'background.paper',
                                        transition: 'border-color 120ms ease, background-color 120ms ease',
                                        '&:hover': { borderColor: ACCENT },
                                    }}
                                >
                                    {selected && (
                                        <CheckIcon sx={{ position: 'absolute', top: 10, right: 10, fontSize: 20, color: ACCENT }} />
                                    )}
                                    <Box sx={{ color: selected ? ACCENT : 'text.secondary', mb: 0.75 }}>{m.icon}</Box>
                                    <Typography sx={{ fontWeight: 700 }}>{m.title}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {m.desc}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Stack>

                    {/* 캡쳐본 (선택) */}
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                        캡쳐본 첨부 <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(선택)</Box>
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                        <Button
                            variant="outlined"
                            startIcon={uploading ? <CircularProgress size={16} /> : <ImageIcon />}
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            sx={{ textTransform: 'none', borderRadius: 2, cursor: 'pointer' }}
                        >
                            {uploading ? '올리는 중…' : proof ? '다시 첨부' : '이미지 첨부'}
                        </Button>
                        {proof && (
                            <Box
                                component="img"
                                src={proof}
                                alt="캡쳐본"
                                sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.08)' }}
                            />
                        )}
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                uploadProof(f);
                            }}
                        />
                    </Stack>

                    <Button
                        onClick={() => mutation.mutate()}
                        disabled={mutation.isPending || uploading || !verification.hasInstagram}
                        variant="contained"
                        size="large"
                        startIcon={mutation.isPending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : status !== 'NONE' ? <ReplayIcon /> : undefined}
                        disableElevation
                        sx={{
                            bgcolor: ACCENT,
                            fontWeight: 800,
                            borderRadius: 2.5,
                            px: 3,
                            textTransform: 'none',
                            '&:hover': { bgcolor: ACCENT_HOVER },
                            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                        }}
                    >
                        {status === 'NONE' ? '인증 요청하기' : '다시 인증 요청하기'}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                        요청하면 심사 대기 상태가 되고, <b>영업일 기준 2~5일</b> 안에 확인돼요. 인스타 주소는 연결된 계정에서 자동
                        확인되니 따로 입력하지 않아도 돼요.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default VerificationScreen;
